import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi, fileApi } from "~/api";
import { folderKeys } from "~/components/side-bar/hooks/use-folder-mutations";
import type { EditTaskRequest } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";
import { getTasksQueryKey, TASK_TOAST_MESSAGES } from "./task-constants";
import { useTaskDialog } from "./use-task-dialog";

type UpdateTaskVariables = {
  taskId: number;
  brandId: number;
  originalFolderId?: string;
  newFolderId?: string;
  body: EditTaskRequest;
  showToastMessage?: boolean;
  silent?: boolean;
};

export const useUpdateTask = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { closeDialog } = useTaskDialog();

  const tasksQueryKey = getTasksQueryKey();

  const { mutateAsync: mutateTask, isPending } = useMutation({
    mutationFn: async ({
      taskId,
      brandId,
      originalFolderId,
      newFolderId,
      body,
      showToastMessage = true,
      silent = false,
    }: UpdateTaskVariables) => {
      const response = await fileApi.client.PUT("/api/Task/{taskId}", {
        params: { path: { taskId } },
        body,
      });

      if (response.error) {
        const errorMessage =
          typeof response.error === "object" && response.error !== null
            ? JSON.stringify(response.error)
            : String(response.error);
        throw new Error(errorMessage);
      }

      if (!silent && originalFolderId !== newFolderId) {
        if (originalFolderId && originalFolderId !== "") {
          const removeResponse = await configurationApi.client.PUT(
            "/api/brand/{brandId}/Folder/{folderId}/remove-tasks",
            {
              params: { path: { brandId, folderId: Number(originalFolderId) } },
              body: {
                folderTasks: [
                  {
                    folderId: Number(originalFolderId),
                    taskId,
                  },
                ],
              },
            }
          );

          if (removeResponse.error) {
            const errorMessage =
              typeof removeResponse.error === "object" && removeResponse.error !== null
                ? JSON.stringify(removeResponse.error)
                : String(removeResponse.error);
            throw new Error(errorMessage);
          }
        }

        if (!silent && newFolderId) {
          const addResponse = await configurationApi.client.PUT(
            "/api/brand/{brandId}/Folder/{folderId}/add-tasks",
            {
              params: { path: { brandId, folderId: Number(newFolderId) } },
              body: {
                folderTasks: [
                  {
                    folderId: Number(newFolderId),
                    taskId,
                  },
                ],
              },
            }
          );

          if (addResponse.error) {
            const errorMessage =
              typeof addResponse.error === "object" && addResponse.error !== null
                ? JSON.stringify(addResponse.error)
                : String(addResponse.error);
            throw new Error(errorMessage);
          }
        }
      }

      return response.data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tasksQueryKey });
      const previousTasks = queryClient.getQueryData(tasksQueryKey);
      // If this is a silent/background update, skip optimistic cache update
      if (!variables.silent) {
        queryClient.setQueryData<TaskManagerDTO[]>(tasksQueryKey, (old) =>
          Array.isArray(old)
            ? old.map((task) =>
                task.id === variables.taskId
                  ? {
                      ...task,
                      name: variables.body.name,
                      path: variables.body.path,
                      taskFields:
                        variables?.body?.taskFields != null
                          ? variables.body.taskFields.map((field) => ({
                              fieldId: field.fieldId,
                              value: field.value,
                              type:
                                task.taskFields?.find((tf) => tf.fieldId === field.fieldId)?.type ||
                                "",
                            }))
                          : task.taskFields,
                    }
                  : task
              )
            : old
        );
      }

      return { previousTasks };
    },
    onError: (err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(tasksQueryKey, context.previousTasks);
      }
      const errorMessage = err instanceof Error ? err.message : String(err);
      showToast("error", errorMessage || TASK_TOAST_MESSAGES.UPDATE_ERROR, "Error");
    },
    onSuccess: (_data, _variables) => {
      if (_variables.showToastMessage) {
        showToast("success", TASK_TOAST_MESSAGES.UPDATE_SUCCESS, "Success");
      }
      if (!_variables.silent) {
        closeDialog?.();
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: tasksQueryKey });
      if (variables.originalFolderId !== variables.newFolderId) {
        queryClient.invalidateQueries({
          queryKey: folderKeys.byBrand(variables.brandId),
        });
      }
    },
  });

  const handleUpdate = async (
    taskId: number,
    taskData: TaskManagerDTO,
    originalFolderId?: string,
    showToastMessage = true,
    silent = false
  ) => {
    const newFolderId =
      typeof taskData.folder === "object" ? String(taskData.folder?.id) : taskData.folder;

    await mutateTask({
      taskId: Number(taskId),
      brandId: Number(taskData.brandId),
      originalFolderId,
      newFolderId: newFolderId || undefined,
      body: {
        name: taskData.name,
        path: taskData.path,
        workflowId: taskData.workflowId,
        taskFields: (taskData.taskFields || []).map((field) => ({
          fieldId: field.fieldId,
          value: field.value,
        })),
      },
      showToastMessage,
      silent,
    });
  };

  return { handleUpdate, isPending };
};
